import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { tournaments as tournamentsApi, tournamentAdmin } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import ShareEvent from '../components/ShareEvent';
import {
  Plus, Swords, BarChart3, Calendar, Users, Edit, AlertTriangle,
  CheckCircle, PlayCircle, Trophy, ChevronRight, Clock,
} from 'lucide-react';
import { getGameColor, getGameName } from '../constants/theme';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const QuickAction = ({ icon: Icon, label, desc, color, onClick, testid }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className="group relative flex-1 min-w-[220px] text-left rounded-2xl border border-[#2a2a2a] bg-[#151515] p-5 transition-all hover:border-[#FF6B35]/50 hover:-translate-y-0.5"
  >
    <div className="flex items-center justify-between">
      <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${color}1a`, color }}>
        <Icon className="w-6 h-6" />
      </div>
      <ChevronRight className="w-5 h-5 text-[#555] group-hover:text-[#FF6B35] transition-colors" />
    </div>
    <h3 className="mt-4 font-bold text-white text-lg">{label}</h3>
    <p className="text-sm text-[#888] mt-0.5">{desc}</p>
  </button>
);

export default function DashboardProPage() {
  const navigate = useNavigate();
  const { user, selectedShop } = useAuth();
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [participantsFor, setParticipantsFor] = useState(null);
  const [participants, setParticipants] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const isAdminRole = user?.role === 'ADMIN' || user?.role === 'EDITOR';
      const params = isAdminRole ? { limit: 200 } : { organizer_id: user?.id, limit: 200 };
      const res = await tournamentsApi.list(params);
      setTournaments(res.data.tournaments || res.data || []);
    } catch {
      toast.error('Erreur chargement du tableau de bord');
    } finally {
      setLoading(false);
    }
  }, [user?.id, user?.role]);

  useEffect(() => { load(); }, [load]);

  const now = new Date();
  const statusOf = (t) => t.status || 'upcoming';
  const isOverdue = (t) => t.date && new Date(t.date) < now && statusOf(t) === 'upcoming';

  const upcoming = tournaments
    .filter(t => statusOf(t) === 'upcoming' && !isOverdue(t))
    .sort((a, b) => new Date(a.date) - new Date(b.date));
  const toClose = tournaments.filter(t => statusOf(t) === 'completed' || isOverdue(t));
  const ongoing = tournaments.filter(t => statusOf(t) === 'in_progress');

  const formatDate = (d) => !d ? '' : new Date(d).toLocaleDateString('fr-FR', {
    weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
  });
  const dayKey = (d) => new Date(d).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });

  // Share helpers
  const openInscrits = async (t) => {
    setParticipantsFor(t);
    try { const res = await tournamentAdmin.getParticipants(t.id); setParticipants(res.data); }
    catch { setParticipants([]); }
  };

  // Group upcoming by day
  const grouped = upcoming.reduce((acc, t) => {
    const k = dayKey(t.date);
    (acc[k] = acc[k] || []).push(t);
    return acc;
  }, {});

  const pendingCount = toClose.length + ongoing.length;

  return (
    <div className="space-y-8" data-testid="dashboard-pro">
      {/* Header avec bannière + logo rond */}
      <div className="relative rounded-2xl overflow-hidden border border-[#2a2a2a]" data-testid="dashboard-header">
        <div className="aspect-[3/1] w-full bg-[#151515]">
          {selectedShop?.banner_url ? (
            <img
              src={selectedShop.banner_url.startsWith('http') ? selectedShop.banner_url : `${process.env.REACT_APP_BACKEND_URL}${selectedShop.banner_url}`}
              alt="Bannière" className="w-full h-full object-contain" data-testid="dashboard-banner"
            />
          ) : (
            <div className="w-full h-full" style={{ background: 'linear-gradient(120deg,#FF6B35 0%,#7B1E00 100%)' }} />
          )}
        </div>
        <div className="px-5 pb-4 -mt-14 sm:-mt-16 flex items-end gap-4">
          <div className="w-28 h-28 sm:w-32 sm:h-32 rounded-full border-4 border-[#0f0f0f] bg-[#1a1a1a] overflow-hidden shrink-0 shadow-lg">
            {selectedShop?.logo_url ? (
              <img
                src={selectedShop.logo_url.startsWith('http') ? selectedShop.logo_url : `${process.env.REACT_APP_BACKEND_URL}${selectedShop.logo_url}`}
                alt="Logo" className="w-full h-full object-cover" data-testid="dashboard-logo"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-[#FF6B35] font-bold text-4xl">{(selectedShop?.name || 'A')[0]}</div>
            )}
          </div>
          <div className="pb-1">
            <h1 className="text-xl sm:text-2xl font-extrabold text-white leading-tight">
              {selectedShop?.name || user?.pseudo || 'Boutique'}
            </h1>
            <p className="text-[#888] text-sm">
              {upcoming.length} événement{upcoming.length > 1 ? 's' : ''} à venir · {pendingCount} action{pendingCount > 1 ? 's' : ''} en attente
            </p>
          </div>
        </div>
      </div>

      {/* Quick actions */}
      <div className="flex flex-wrap gap-4">
        <QuickAction icon={Plus} label="Créer un événement" desc="Nouveau tournoi ou partie" color="#FF6B35"
          onClick={() => navigate('/create-tournament')} testid="qa-create-event" />
        <QuickAction icon={Swords} label="Accéder aux parties" desc="Gérer rondes & appariements" color="#4ECDC4"
          onClick={() => navigate('/shop/tournaments?tab=ongoing')} testid="qa-games" />
        <QuickAction icon={BarChart3} label="Voir les statistiques" desc="Performance de la boutique" color="#7B68EE"
          onClick={() => navigate('/stats')} testid="qa-stats" />
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-40"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF6B35]" /></div>
      ) : (
        <>
          {/* Actions en attente */}
          {pendingCount > 0 && (
            <section data-testid="pending-actions">
              <h2 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-400" /> Actions en attente
                <Badge className="bg-amber-500/15 text-amber-400 ml-1">{pendingCount}</Badge>
              </h2>
              <div className="space-y-2">
                {toClose.map((t) => (
                  <div key={t.id} className="flex items-center gap-3 rounded-xl bg-[#151515] border border-amber-500/25 px-4 py-3" data-testid={`pending-close-${t.id}`}>
                    <div className="w-1 h-10 rounded-full shrink-0" style={{ backgroundColor: getGameColor(t.game) }} />
                    <div className="flex-1 min-w-0">
                      <span className="text-white font-semibold text-sm truncate block">{t.name}</span>
                      <span className="text-xs text-[#888] flex items-center gap-1"><CheckCircle className="w-3 h-3 text-amber-400" />Terminé — à clôturer · {(t.participants || []).length} inscrits</span>
                    </div>
                    <Button size="sm" onClick={() => navigate('/shop/tournaments?tab=toclose')} className="bg-amber-500 hover:bg-amber-600 text-black shrink-0">Clôturer</Button>
                  </div>
                ))}
                {ongoing.map((t) => (
                  <div key={t.id} className="flex items-center gap-3 rounded-xl bg-[#151515] border border-green-500/25 px-4 py-3" data-testid={`pending-ongoing-${t.id}`}>
                    <div className="w-1 h-10 rounded-full shrink-0" style={{ backgroundColor: getGameColor(t.game) }} />
                    <div className="flex-1 min-w-0">
                      <span className="text-white font-semibold text-sm truncate block">{t.name}</span>
                      <span className="text-xs text-[#888] flex items-center gap-1"><PlayCircle className="w-3 h-3 text-green-400" />En cours — à gérer / classer</span>
                    </div>
                    <Button size="sm" onClick={() => navigate(`/tournament/${t.id}/manage`)} className="bg-green-600 hover:bg-green-700 shrink-0">Gérer</Button>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Calendrier / prochains événements */}
          <section data-testid="upcoming-calendar">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-bold text-white flex items-center gap-2"><Calendar className="w-5 h-5 text-[#FF6B35]" /> Prochains événements</h2>
              <Button variant="ghost" size="sm" onClick={() => navigate('/shop/tournaments?tab=active')} className="text-[#888] hover:text-white">Tout voir</Button>
            </div>
            {upcoming.length === 0 ? (
              <Card className="bg-[#151515] border-[#2a2a2a]"><CardContent className="p-8 text-center">
                <Trophy className="w-10 h-10 text-[#333] mx-auto mb-3" />
                <p className="text-[#888]">Aucun événement à venir.</p>
                <Button onClick={() => navigate('/create-tournament')} className="mt-4 bg-[#FF6B35] hover:bg-[#E55A25] gap-2"><Plus className="w-4 h-4" />Créer un événement</Button>
              </CardContent></Card>
            ) : (
              <div className="space-y-5">
                {Object.entries(grouped).map(([day, list]) => (
                  <div key={day}>
                    <p className="text-xs uppercase tracking-wide text-[#666] font-semibold mb-2 capitalize">{day}</p>
                    <div className="space-y-2">
                      {list.map((t) => (
                        <div key={t.id} className="flex items-center gap-3 rounded-xl bg-[#151515] border border-[#2a2a2a] px-4 py-3 hover:border-[#FF6B35]/30 transition-colors" data-testid={`event-row-${t.id}`}>
                          <div className="flex flex-col items-center justify-center w-14 shrink-0 text-center">
                            <span className="text-[#FF6B35] font-bold text-lg leading-none">{new Date(t.date).getDate()}</span>
                            <span className="text-[10px] text-[#888] uppercase">{new Date(t.date).toLocaleDateString('fr-FR', { month: 'short' })}</span>
                            <span className="text-[10px] text-[#666] mt-0.5">{new Date(t.date).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</span>
                          </div>
                          <div className="w-px h-10 bg-[#2a2a2a]" />
                          <div className="flex-1 min-w-0 cursor-pointer" onClick={() => navigate(`/player/event/tournament/${t.id}`)} data-testid={`event-open-${t.id}`}>
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-white font-semibold text-sm truncate max-w-[280px]">{t.name}</span>
                              <Badge className="text-[10px] px-1.5 py-0" style={{ backgroundColor: `${getGameColor(t.game)}20`, color: getGameColor(t.game) }}>{getGameName(t.game)}</Badge>
                              {(() => { const p = t.publish_at ? new Date(t.publish_at) : null; return (p && p > now) ? <Badge className="bg-[#7B68EE]/15 text-[#7B68EE] text-[10px] gap-1"><Clock className="w-3 h-3" />Programmé</Badge> : null; })()}
                            </div>
                            <span className="text-xs text-[#888] flex items-center gap-1 mt-1"><Users className="w-3 h-3" />{(t.participants || []).length}/{t.capacity || '?'} inscrits {t.price > 0 ? `· ${t.price}€` : ''}</span>
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            <Button variant="ghost" size="sm" onClick={() => openInscrits(t)} className="text-[#888] hover:text-[#4ECDC4] hover:bg-[#4ECDC4]/10 gap-1 h-8 px-2" data-testid={`event-inscrits-${t.id}`}>
                              <Users className="w-4 h-4" /><span className="hidden md:inline text-xs">Inscrits</span>
                            </Button>
                            <Button variant="ghost" size="icon" onClick={() => navigate(`/shop/tournaments?tab=active&edit=${t.id}`)} className="text-[#888] hover:text-[#FF6B35] hover:bg-[#FF6B35]/10 h-8 w-8" data-testid={`event-edit-${t.id}`} title="Modifier"><Edit className="w-4 h-4" /></Button>
                            <ShareEvent t={t} />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        </>
      )}

      {/* Inscrits dialog */}
      <Dialog open={!!participantsFor} onOpenChange={() => { setParticipantsFor(null); setParticipants([]); }}>
        <DialogContent className="bg-[#1a1a1a] border-[#333] text-white max-w-lg">
          <DialogHeader>
            <DialogTitle>Inscrits — {participantsFor?.name}</DialogTitle>
            <DialogDescription className="text-[#666]">{participants.length} / {participantsFor?.capacity || '?'} places</DialogDescription>
          </DialogHeader>
          <div className="max-h-80 overflow-y-auto space-y-1.5">
            {participants.map((p) => (
              <div key={p.id} className="flex items-center justify-between px-3 py-2.5 rounded-lg bg-[#252525]">
                <span className="text-white font-medium text-sm">{p.pseudo || p.name}</span>
                <span className="text-[#666] text-xs">{p.email}</span>
              </div>
            ))}
            {participants.length === 0 && <p className="text-[#666] text-center py-6 text-sm">Aucun inscrit</p>}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
